import type { TUseSubLog } from '@/contexts/SiteSpecificContext/hooks/subLog'

import { useSiteSpecific } from '@/contexts/SiteSpecificContext'
import { PaymentPlatformPlatformName } from '@/contexts/SiteSpecificContext/types'
import { useUserPayPlatformMapping } from '@/hooks/userGroup/userPayPlatformMapping'
import { api } from '@/utils/api'


import { useMemo } from 'react';
export function useListPaymentPlatform({ plan }: { plan: TUseSubLog }) {
  const {
    creditsPlan,
    planPlatformInfoEnable,
    planPlatformName,
    planPlatformInfoVisible,
  } = plan

  const { data, isInitialLoading, refetch, isLoading, isFetching } =
    api.product.listPaymentPlatform.useQuery()

  const { user } = useSiteSpecific()
  const { userGroup } = user

  const initPaymentPlatformList = useMemo(() => {
    if (data) {
      return data?.paymentPlatformList || []
    }
    return []
  }, [data])

  const stripePlatForm = useMemo(() => {
    // return {
    //   platform_name: '',
    //   publishable_key: '',
    // }
    return initPaymentPlatformList.find(
      (item) => item.platformName === 'Stripe',
    )
  }, [initPaymentPlatformList])

  const fastspringPlatForm = useMemo(() => {
    // return {
    //   platform_name: '',
    //   publishable_key: '',
    // }
    return initPaymentPlatformList.find(
      (item) => item.platformName === 'Fastspring',
    )
  }, [initPaymentPlatformList])

  const paypalPlatForm = useMemo(() => {
    return initPaymentPlatformList.find(
      (item) => item.platformName === 'Paypal',
    )
  }, [initPaymentPlatformList])

  const payinsiderPlatForm = useMemo(() => {
    return initPaymentPlatformList.find(
      (item) => item.platformName === 'Payinsider',
    )
  }, [initPaymentPlatformList])

  const { getPayPlatformGroupByUserGroup, userPayPlatformMapping } =
    useUserPayPlatformMapping()

  /* 获取当前用户可用的支付列表 */
  const paymentPlatformList = useMemo(() => {
    const userTag = getPayPlatformGroupByUserGroup(userGroup)
    const tagPlanGroup = userPayPlatformMapping[userTag]
    const { platformName } = tagPlanGroup
    const listA = [stripePlatForm, paypalPlatForm].filter(
      (item) => !!item?.platformName,
    ) as typeof initPaymentPlatformList
    const listB = [payinsiderPlatForm, paypalPlatForm].filter(
      (item) => !!item?.platformName,
    ) as typeof initPaymentPlatformList

    const platformMap = {
      [PaymentPlatformPlatformName.Fastspring]: fastspringPlatForm,
      [PaymentPlatformPlatformName.Paypal]: paypalPlatForm,
      [PaymentPlatformPlatformName.Stripe]: stripePlatForm,
      [PaymentPlatformPlatformName.Payinsider]: payinsiderPlatForm,
    }

    const validPlatformNames = Object.keys(platformMap)

    // 已购买套餐，使用套餐的支付方式
    if (
      planPlatformInfoEnable &&
      planPlatformName &&
      validPlatformNames.includes(planPlatformName) &&
      planPlatformInfoVisible
    ) {
      return [platformMap[planPlatformName]]
    }

    if (platformName === PaymentPlatformPlatformName.Stripe) {
      if (!stripePlatForm) {
        return listB
      }
      return listA
    }

    if (platformName === PaymentPlatformPlatformName.Payinsider) {
      if (!payinsiderPlatForm) {
        return listA
      }
      // 本地测试可以只用stripe
      // if(IS_DEV){
      //   return listA
      // }
      return listB
    }
    return listB
  }, [
    userGroup,
    userPayPlatformMapping,
    stripePlatForm,
    paypalPlatForm,
    fastspringPlatForm,
    creditsPlan,
    planPlatformInfoEnable,
    planPlatformName,
    payinsiderPlatForm,
  ])

  /**
   * @description: 首先使用的付款方式,platform_name 为空则是在请求中
   */
  const firstPaymentPlatform = useMemo(() => {
    if (paymentPlatformList[0]) {
      return paymentPlatformList[0]
    }
    return {
      platform_name: '',
      publishable_key: '',
    }
  }, [paymentPlatformList]) as (typeof initPaymentPlatformList)[number]

  // useEffect(() => {
  //   console.log(firstPaymentPlatform, 'firstPaymentPlatform')
  //   console.log(paymentPlatformList, 'paymentPlatformList')
  // }, [firstPaymentPlatform, paymentPlatformList])

  const firstPaymentPlatformIsFastspring =
    firstPaymentPlatform?.platformName ===
    PaymentPlatformPlatformName.Fastspring

  const firstPaymentPlatformIsStripe =
    firstPaymentPlatform?.platformName === PaymentPlatformPlatformName.Stripe

  const firstPaymentPlatformIsPaypal =
    firstPaymentPlatform?.platformName === PaymentPlatformPlatformName.Paypal

  const firstPaymentPlatformIsPayinsider =
    firstPaymentPlatform?.platformName ===
    PaymentPlatformPlatformName.Payinsider

  return {
    initPaymentPlatformList,
    paymentPlatformList,
    isInitialLoading,
    isLoading,
    refetch,
    isFetching,
    firstPaymentPlatform,
    paypalPlatForm,
    stripePlatForm,
    fastspringPlatForm,
    firstPaymentPlatformIsFastspring,
    firstPaymentPlatformIsStripe,
    firstPaymentPlatformIsPaypal,
    firstPaymentPlatformIsPayinsider,
  }
}

export type TUseListPaymentPlatform = ReturnType<typeof useListPaymentPlatform>
